import { type ReactNode } from "react";
import Icon from "./Icon";

interface TopBarProps {
    /** Show back arrow; clicking fires onBack */
    showBack?: boolean;
    onBack?: () => void;

    /** Title displayed at center — defaults to "CODEV" branding */
    title?: string;

    /** Subtitle (small text below title, used on detail pages) */
    subtitle?: string;

    /** Right-hand slot (e.g. wallet icon, menu icon) */
    rightSlot?: ReactNode;

    /** "legacy" = old neon design, "minimal" = new redesign v2 */
    variant?: "legacy" | "minimal";

    /** Make the top bar background transparent (e.g. for pages with confetti behind it) */
    transparentBg?: boolean;
}

export default function TopBar({
    showBack = false,
    onBack,
    title,
    subtitle,
    rightSlot,
    variant = "legacy",
    transparentBg = false,
}: TopBarProps): ReactNode {
    if (variant === "minimal") {
        return (
            <>
                <header
                    id="top-bar"
                    className={`fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] flex items-center px-[clamp(16px,2.6dvh,24px)] py-[clamp(12px,2.1dvh,20px)] z-50 ${transparentBg ? "bg-transparent" : "bg-[#0a0a0a]"}`}
                >
                    {/* Left slot */}
                    <div className="flex w-8 shrink-0 items-center">
                        {showBack && (
                            <button
                                id="top-bar-back"
                                onClick={onBack}
                                className="flex size-8 items-center justify-center text-white transition-colors active:scale-95"
                            >
                                <Icon name="back-arrow" size={32} />
                            </button>
                        )}
                    </div>

                    {/* Center */}
                    <div className="flex-1 flex justify-center">
                        {title !== "" && (
                            <h2 className="text-white text-[clamp(18px,2.1dvh,20px)] font-medium leading-[1.15] tracking-[-0.4px]">
                                {title ?? "Coin Details"}
                            </h2>
                        )}
                    </div>

                    {/* Right slot */}
                    <div className="w-8 flex justify-end">
                        {rightSlot}
                    </div>
                </header>
                {/* Spacer */}
                <div className="h-[clamp(56px,7.7dvh,72px)] shrink-0" />
            </>
        );
    }

    // Legacy variant (old design)
    return (
        <>
            <header
                id="top-bar"
                className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] flex items-center bg-background-dark/80 px-4 py-3 z-50 backdrop-blur-md border-b border-white/5"
            >
                {/* Left slot */}
                <div className="flex w-10 shrink-0 items-center">
                    {showBack && (
                        <button
                            id="top-bar-back"
                            onClick={onBack}
                            className="flex size-10 items-center justify-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white transition-colors active:scale-95"
                        >
                            <Icon name="chevron-left" size={20} className="opacity-70" />
                        </button>
                    )}
                </div>

                {/* Center */}
                <div className="flex-1 flex flex-col items-center">
                    {subtitle ? (
                        <>
                            <h2 className="text-white text-[10px] font-extrabold leading-tight tracking-widest uppercase italic opacity-60">
                                {subtitle}
                            </h2>
                        </>
                    ) : (
                        <div className="flex items-center">
                            <img
                                src="/logo_icon.svg"
                                alt={title ?? "CODEV"}
                                className="h-10 w-10 shrink-0"
                            />
                            <h2 className="text-lg uppercase font-semibold leading-none tracking-[0.05em] bg-[linear-gradient(to_right,#a8ff3e_0%,#ffec70_45%,#ff7ee6_65%,#9d1eff_100%)] inline-block text-transparent bg-clip-text">
                                {title ?? "CODEV"}
                            </h2>
                        </div>
                    )}
                </div>

                {/* Right slot */}
                <div className="w-10 flex justify-end">
                    {rightSlot}
                </div>
            </header>
            {/* Spacer to prevent content from hiding under fixed header */}
            <div className="h-16 shrink-0" />
        </>
    );
}
